import React, { useState, useEffect, useRef, useCallback, useMemo, Profiler } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { isNative, isAndroid, androidInset, getBrowserDeviceType, isDesktopBrowser } from './utils/platformUtils';
import { configureStatusBar } from './utils/statusBarUtils';
import { useUserSetting } from './data/hooks/useUserSetting';
import { usePosts, loadPosts, markUpdateToastShown, shouldShowUpdateToast, releaseForVersion } from './data/posts';
import { appVersion } from './components/Settings';
import { onRenderProfile } from './utils/lessonPerfTrace';
import { SplashScreen } from '@capacitor/splash-screen';
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Typography,
  Box,
  Button,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  Divider,
  Grid,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import PlayNoteGrid from './components/PlayNoteGrid';
import MusicPanelContainer from './components/MusicPanel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import LockIcon from '@mui/icons-material/Lock';
import PlayNeck from './components/PlayNeck';
import ModeInfoDisplay from './components/ModeInfoDisplay';
import AudioMixer from './components/AudioMixer';
import PlaybackMixer from './components/PlaybackMixer';
import ShortcutsHelp from './components/ShortcutsHelp';
import { isShortcutTargetBlocked, resolveDesktopShortcut, TOGGLE_MIXER_EVENT, TOGGLE_NECK_VIEW_EVENT, TOGGLE_SHORTCUTS_EVENT } from './utils/desktopShortcuts';
import Learn from './components/Learn';
import BackingTrackPlayer from './components/BackingTrackPlayer';
import LessonPlayer from './components/LessonPlayer';
import PatternsPanel from './components/PatternsPanel';
import SavePatternDialog from './components/SavePatternDialog';
import YouPage from './components/YouPage';
import audioEngine from './utils/audioEngine';
import { useSynthPlayer } from './hooks/useSynthPlayer';
import { useAutoScale } from './hooks/useAutoScale';
import logService from './utils/logService';
import { collectFretboardState } from './utils/fretboardStateCollector';
import * as Tone from 'tone';
import { ALL_NOTES, ENHARMONIC_EQUIVALENTS, MODE_NAMES_BY_SCALE, SCALE_TYPES, MOOD_TO_SCALE, ALL_MOODS, getNotesInMode, getChordDescriptorsInMode, getMoodsForScale, getModesForScaleTypeInTier, MODES_BY_TIER, getProgressionBaseName, getProgressionNumeralsInMode, getDefaultProgression, RANDOM_PROGRESSION, resolveAllNotesIndex } from './utils/musicTheory';
import TieredScaleSelector from './components/TieredScaleSelector';
import UnlockSheet from './components/UnlockSheet';
import SignUpSheet from './components/SignUpSheet';
import { getProgressionNames, getUserProgressions, saveUserProgression, CHORD_PROGRESSIONS } from './utils/chordProgressions';
import { getCompatibleProgressions } from './utils/progressionCompatibility';
import { getCompatibleImproScales, nextImproScale, formatScaleLabel, resolveBackingScale } from './utils/improScales';
import ImproScalesDialog from './components/ImproScalesDialog';
import multiNotePlayer from './utils/multiNotePlayer';
import { initAudioSessionGuard } from './utils/audioSessionGuard';
import { track, resetSessionId } from './utils/analytics';
import { getAllBackingTracksSorted, isTrackUnlocked } from './data/artistContent';
import usePackPurchases from './hooks/usePackPurchases';
import { useEntitlement } from './hooks/useEntitlement';
import { usePatreonReturn } from './hooks/usePatreonReturn';
import { useOAuthReturn } from './hooks/useOAuthReturn';
import { useInviteLink, guestSwitchPrompt } from './hooks/useInviteLink';
import { useSilentSignInToast } from './hooks/useSilentSignInToast';
import { usePendingUnlock } from './hooks/usePendingUnlock';
import { markPendingUnlock, clearPendingUnlock } from './lib/authAccount';
import { useAndroidBackButton } from './hooks/useAndroidBackButton';
import { App as CapApp } from '@capacitor/app';
import './App.css';
import './styles/common.css';
import './styles/icon-size.css'; // Import des styles pour agrandir les icônes

// Import outline icons

import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import UnreadDot from './components/UnreadDot';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import FolderIcon from '@mui/icons-material/Folder';
import CasinoIcon from '@mui/icons-material/Casino';
import CustomToast from './components/CustomToast';

import RadialFabMenu from './components/RadialFabMenu';
import Tip, { coachBlinkSx } from './components/Tip';
import useGettingStarted from './hooks/useGettingStarted';
import { publishPlayChord, publishActiveChordNote, readPlayChord, useIsChordSounding } from './hooks/usePlayChord';
import useDiscoveryTips from './hooks/useDiscoveryTips';
import HeaderPills from './components/HeaderPills';
import theme from './theme';
import { SELECTED_BG, SELECTED_FG, SELECTED_BG_HOVER } from './config/uiTheme';

// Hauteur reservee sous le contenu pour la barre du bas, qui est en position
// fixed : ses 64px, plus les 10px dont elle est decollee en natif, plus une
// respiration. La safe area s'y ajoute a l'usage.
const BOTTOM_NAV_RESERVE = 82;

// Largeur du rail de gauche qui remplace la barre du bas sur navigateur de
// bureau. Le contenu se decale d'autant au lieu de reserver du vide en bas.
const DESKTOP_RAIL_WIDTH = 76;
// Largeur fixe du manche : 24 frettes de 60px plus les labels de cordes
const NECK_MAX_WIDTH = 1470;
// Respiration gardee de chaque cote du manche quand la largeur le fait reduire
const NECK_SIDE_MARGIN = 16;

// Cote reserve en bas a droite pour le menu radial en mode compact (56px de
// bouton, 5px de marge, le reste en respiration).
const RADIAL_FAB_RESERVE = 68;

// Pendant une lecon, la peinture du manche est gelee : ce noop stable remplace
// setSelectedNotes sans faire recalculer les callbacks du manche a chaque rendu.
const noopSelectedNotesChange = () => {};

// Composant AppContent
const AppContent = () => {

  const { purchasedPacks } = usePackPurchases();
  const { isRootNoteUnlocked, isModeUnlocked } = useEntitlement();
  // Contenu verrouille tape par l'utilisateur -> bottom sheet preview/achat.
  const [unlockSheetContext, setUnlockSheetContext] = useState(null);
  // Inscription proposee apres un code d'invitation valide : l'acces vient
  // d'etre pose sur cet appareil, le compte est ce qui l'emmene ailleurs.
  const [signUpOpen, setSignUpOpen] = useState(false);
  const [signUpVariant, setSignUpVariant] = useState('beta');

  // Toute fermeture du verrou passe par ici. La marque du parcours d'achat n'a
  // de sens que tant qu'il reste ouvert : ferme, le parcours s'est conclu sans
  // rechargement (achat abouti, compte cree par code, ou depart pur et simple),
  // et une marque oubliee rouvrirait un verrou fantome au lancement suivant.
  const closeUnlockSheet = useCallback(() => {
    clearPendingUnlock();
    setUnlockSheetContext(null);
  }, []);

  // Configure StatusBar and hide SplashScreen for native apps
  useEffect(() => {
    const configureNativeApp = async () => {
      if (isNative()) {
        try {
          // Configure status bar
          await configureStatusBar();
          
          // Hide splash screen
          await SplashScreen.hide();
        } catch (error) {
          console.error('Failed to configure native app:', error);
        }
      }
    };
    configureNativeApp();
  }, []);
  
  // Analytics: session lifecycle tracking
  useEffect(() => {
    const isFirstOpen = localStorage.getItem('hasOpenedBefore') !== 'true';
    track('app_opened', { is_first_open: isFirstOpen });
    if (isFirstOpen) localStorage.setItem('hasOpenedBefore', 'true');

    let sessionStart = Date.now();

    // Native iOS/Android: use Capacitor App listener (fires reliably on suspend/resume)
    let nativeListener = null;
    if (isNative()) {
      nativeListener = CapApp.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          track('app_backgrounded', { session_duration_s: Math.round((Date.now() - sessionStart) / 1000) }, { sendBeacon: true });
        } else {
          resetSessionId();
          sessionStart = Date.now();
          track('app_foregrounded');
          loadPosts();
        }
      });
    }

    // Web fallback: visibilitychange + beforeunload
    const handleVisibility = () => {
      if (isNative()) return; // handled by Capacitor listener above
      if (document.visibilityState === 'hidden') {
        track('app_backgrounded', { session_duration_s: Math.round((Date.now() - sessionStart) / 1000) }, { sendBeacon: true });
      } else {
        sessionStart = Date.now();
        track('app_foregrounded');
        loadPosts();
      }
    };

    const handleUnload = () => {
      track('app_closed', { session_duration_s: Math.round((Date.now() - sessionStart) / 1000) }, { sendBeacon: true });
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleUnload);
      if (nativeListener) nativeListener.then(h => h.remove());
    };
  }, []);

  // Initialize with A Major Ionian as default: the featured backing (Per Nilsson
  // "A Major Progression") is in A, so the first Jam visit lands on a perfect match.
  const [rootNote, setRootNote] = useState('A');
  const [currentScaleType, setCurrentScaleType] = useState('Major');
  const [currentMode, setCurrentMode] = useState('Ionian');
  const [currentMood, setCurrentMood] = useState('Happy');
  // Note qui sonne, accord actif et accord affiche pour le motif vivent dans le
  // magasin usePlayChord, pas ici : un accord ne doit pas reexecuter App.
  const [initialized, setInitialized] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  // Settings read from the user mirror, synced.
  const [playHighOctave] = useUserSetting('playHighOctave', true);
  // Bass mode (Per Nilsson): how the low bass behaves under the voicings.
  //  'none'   - no bass at all, only the chord tones in the register window
  //  'static' - pedal point: hold the scale tonic in the bass (the app's drone identity)
  //  'follow' - the bass tracks each chord's own root
  // Defaults to 'static'. The legacy upScale.staticBass fallback is handled by the
  // one-shot migration, which already wrote a derived bassMode into user_data.
  const [bassMode, setBassMode] = useUserSetting('upScale.bassMode', 'static');
  // Chord voicing toggle: '1' for a single-note drone, '3' for triads, '7' for
  // 7th chords. Cycles 1 -> 3 -> 7 -> 1. Triads by default per PLAYGROUND v1.
  const [chordExtension, setChordExtension] = useUserSetting('upScale.chordExtension', '3');
  const setChordExtensionValue = (value) => {
    if (!['1', '3', '7'].includes(value)) return;
    setChordExtension(value);
    track('chord_extension_toggled', { chord_extension: value });
  };
  const handleChordExtensionToggle = () => {
    setChordExtensionValue(chordExtension === '1' ? '3' : chordExtension === '3' ? '7' : '1');
  };
  // Start on the default progression of the starting mode, not a fixed demo preset
  // (which was a Phrygian progression shown on the Ionian start screen) and not
  // AUTO: the first screen must show the simplest, most readable cadence. Same
  // rule as every scale change, which all go through getDefaultProgression.
  // Lazy init: falls back to null if the content mirror isn't hydrated yet, and
  // NoteGrid then resolves the same default as soon as the scale is available.
  const [currentProgression, setCurrentProgression] = useState(
    () => getDefaultProgression(currentMode)?.name || null
  );
  const [isProgressionPlaying, setIsProgressionPlaying] = useState(false);
  const progressionWasActiveRef = useRef(false);
  const [isProgressionPaused, setIsProgressionPaused] = useState(false);
  // Gamme posee sur le manche pendant qu'une progression tourne. null = le
  // manche suit la gamme harmonique. Elle ne touche ni les accords ni le son :
  // la progression continue de sonner sur rootNote/currentMode, seule la
  // visualisation change. C'est le pendant du verrou de gamme du mode jam, ou
  // le backing audio est fige et ou seul l'affichage bouge.
  const [improScale, setImproScale] = useState(null); // null | { root, mode }
  const [improScalesOpen, setImproScalesOpen] = useState(false);
  const [activePanelId, setActivePanelId] = useState(null);

  // Coup de pouce au premier lancement : l'automate et ses delais vivent dans le
  // hook, ici on ne fait que lui donner de quoi savoir si le drone est audible.
  // Le guide ne regarde que le fait qu'un accord sonne, pas lequel : s'abonner
  // au booleen laisse les enchainements d'accords hors du rendu de App.
  const isChordSounding = useIsChordSounding();
  const {
    coachStep,
    onChordTapped: onCoachChordTapped,
    onPlayTapped: onCoachPlayTapped,
    onJamTapped: onCoachJamTapped,
    dismissTip: onCoachTipDismiss
  } = useGettingStarted({
    isChordSounding,
    isProgressionPlaying: isProgressionPlaying || isProgressionPaused,
    activePanelId,
    currentPage
  });

  // Les deux gestes invisibles du manche, reveles apres le guide seulement, un
  // seul par session et jamais dans les premieres secondes.
  const { discoveryTip, dismissDiscoveryTip } = useDiscoveryTips({ currentPage, activePanelId });

  const [tabValue, setTabValue] = useState(0); // For the top tabs (0 = Play, 1 = Learn)
  const [savePatternDialogOpen, setSavePatternDialogOpen] = useState(false);
  const [savePatternSuccess, setSavePatternSuccess] = useState(false);
  const [showPatternSnackbar, setShowPatternSnackbar] = useState(false);

  const { unreadCount, posts: allPosts } = usePosts();
  const [updateToast, setUpdateToast] = useState(null);

  useEffect(() => {
    loadPosts();
  }, []);

  // La version n'est marquee vue que si le joueur touche le toast ou le ferme :
  // laisser passer les 8 secondes en regardant le manche ne doit pas consommer
  // l'annonce, elle revient au lancement suivant. Le garde de session evite
  // qu'un rechargement des posts la represente dans la meme session.
  const updateToastSeenRef = useRef(false);

  useEffect(() => {
    if (updateToastSeenRef.current) return;
    const release = releaseForVersion(allPosts, appVersion);
    if (!release || !shouldShowUpdateToast(allPosts, appVersion)) return;
    updateToastSeenRef.current = true;
    setUpdateToast(release);
  }, [allPosts]);

  // Fermeture volontaire (croix ou ouverture du hub) : l'annonce a ete vue.
  const dismissUpdateToast = (seen) => {
    if (seen) markUpdateToastShown(appVersion);
    setUpdateToast(null);
  };

  // Retour de l'autorisation Patreon (query web ou deep link natif) : reclame le
  // nonce avec le JWT courant, ce qui deverrouille l'app sans attendre un pull.
  const { notice: patreonNotice, dismissNotice: dismissPatreonNotice } = usePatreonReturn();
  // Retour du login Google (linkIdentity) : echange le code et met a jour la session.
  const {
    notice: oauthNotice,
    dismissNotice: dismissOauthNotice,
    pending: oauthPending,
  } = useOAuthReturn();
  // Lien d'invitation ouvert dans l'app installee : la session s'ouvre seule, il
  // n'y a ni code a saisir ni identite a choisir. Ne rend un message que lorsque
  // le lien ne vaut plus rien.
  const {
    notice: inviteNotice,
    dismissNotice: dismissInviteNotice,
    pending: invitePending,
    guestConfirm: inviteGuestConfirm,
  } = useInviteLink();
  // Connexion arrivee sans geste (lien personnel, referrer) : l'app se recharge
  // et revient sous un autre compte. Le toast dit lequel.
  const { notice: signedInNotice, dismissNotice: dismissSignedInNotice } = useSilentSignInToast();
  // Verrou interrompu par l'etape compte, puis par le rechargement du retour
  // Google : le contexte a survecu dans le stockage, il n'y a qu'a rouvrir.
  const pendingUnlockContext = usePendingUnlock();
  // Le verrou reprend la place qu'il avait, et rien de plus : la caisse ne
  // s'ouvre pas toute seule au retour d'un rechargement. Un verrou deja a
  // l'ecran gagne, l'utilisateur vient de le demander lui-meme.
  useEffect(() => {
    if (pendingUnlockContext) setUnlockSheetContext((current) => current || pendingUnlockContext);
  }, [pendingUnlockContext]);
  const notesGridRef = useRef(null);
  const drawerPaperRef = useRef(null);

  // Jam mode state for backing tracks
  const [activeBackingTrack, setActiveBackingTrack] = useState(null);
  const [activeBackingTrackArtist, setActiveBackingTrackArtist] = useState(null);
  // Verrou de type de gamme du backing (null = suivre les gammes declarees par
  // les marqueurs). Pre-arme par le rung "Jam it your way" de la carte, puis
  // regle par le dialogue du player ou par la pilule du header.
  const [activeBackingTrackJamScale, setActiveBackingTrackJamScale] = useState(null);
  // Origine du verrou : true = pilule du header, soumis a la pause par section ;
  // false = liste de l'auteur (dialogue du player, palier), applique tel quel.
  const [backingLockChecked, setBackingLockChecked] = useState(false);
  // Section du backing en cours { name, root, scale } : sa gamme declaree, pas
  // celle du manche, qui vaut le verrou quand il s'applique.
  const [backingSection, setBackingSection] = useState(null);
  const [backingScalesOpen, setBackingScalesOpen] = useState(false);
  const [isBackingTrackPlaying, setIsBackingTrackPlaying] = useState(false);
  const backingTrackAudioRef = useRef(null);

  // Lesson player (lecons Guitar Pro) : prend la main sur l'ecran Play comme un
  // backing. Les notes de la section et du pas courant ne passent pas par l'etat
  // de App : LessonPlayer les publie dans useLessonNeck, LessonNeck les lit et
  // reste le seul composant qui se rend a chaque pas. selectedNotes n'est ni
  // affiche ni modifiable le temps de la lecon, et reste intact jusqu'a la sortie.
  const [activeLesson, setActiveLesson] = useState(null);
  const [activeLessonArtist, setActiveLessonArtist] = useState(null);
  const [lessonReturnPackId, setLessonReturnPackId] = useState(null);
  const [isLessonPlaying, setIsLessonPlaying] = useState(false);
  const lessonPlayerRef = useRef(null);
  const youPageRef = useRef(null);
  const learnPageRef = useRef(null);
  // Gamme de Play avant une lecon non transposable, restauree a la sortie.
  const lessonRestoreRef = useRef(null);

  // Use the unified SynthPlayer hook for metronome functionality.
  // subscribeBeat: false -> App does not re-render on every beat. The beat
  // indicator lives inside NoteGrid, which has its own useSynthPlayer() instance.
  const synthPlayer = useSynthPlayer({ subscribeBeat: false });

  // Mise en page bureau : lue une seule fois au montage et jamais recalculee.
  // Elle conditionne la place des portails du manche, qui ne doivent pas changer
  // de parent au milieu d'une session.
  const [desktopBrowser] = useState(isDesktopBrowser);

  // Auto-scale play area to fit small screens without scrolling
  // Sur bureau le rail de gauche retire de la largeur au manche, qui est fixe :
  // l'auto-scale reduit alors le bloc comme le ferait un zoom du navigateur.
  const { scale: playAreaScale, fits: playAreaFits, widthLimited: playAreaWidthLimited, containerRef: playAreaContainerRef, contentRef: playAreaContentRef } = useAutoScale(
    activeLesson ? 'lesson' : 'play',
    desktopBrowser ? { minContentWidth: NECK_MAX_WIDTH + 2 * NECK_SIDE_MARGIN } : undefined
  );

  // Metronome state. Settings (tempo, time signature, volume, visual-only) are
  // remembered across sessions in the user mirror; the on/off toggle is not.
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [metronomeBPM, setMetronomeBPM] = useUserSetting('upScale.metronome.bpm', 135);
  const [metronomeTimeSignature, setMetronomeTimeSignature] = useUserSetting('upScale.metronome.timeSignature', '4/4');
  const [metronomeVolume, setMetronomeVolume] = useUserSetting('upScale.metronome.volume', 0.7);
  const [metronomeVisualOnly, setMetronomeVisualOnly] = useUserSetting('upScale.metronome.visualOnly', false);
  const [barsPerChord, setBarsPerChord] = useState(2);
  // Recording state
  const [isRecordArmed, setIsRecordArmed] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const recordStartTimeRef = useRef(null);
  const recordedEventsRef = useRef([]);

  // Pre-rendering state
  const [isPreRendering, setIsPreRendering] = useState(false);
  const [preRenderProgress, setPreRenderProgress] = useState(0);

  // HCMODE: horizontal compact mode (phone landscape — not enough vertical space).
  // Jamais sur tablette : depuis iPadOS 26 une fenetre redimensionnee peut passer
  // sous le seuil, et masquer la bottom-nav y coupe l'acces a Jam et Settings.
  // Jamais non plus sur navigateur de bureau : une fenetre basse n'est pas un
  // telephone couche.
  const [isCompactMode, setIsCompactMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (desktopBrowser) return false;
    const w = window.innerWidth || 0;
    const h = window.innerHeight || 0;
    const isLandscape = w > h;
    const isPhone = Math.min(w, h) < 600 && getBrowserDeviceType() !== 'tablet';
    return isLandscape && isPhone;
  });
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth || document.documentElement.clientWidth || 0;
      const h = window.innerHeight || document.documentElement.clientHeight || 0;
      const isLandscape = w > h;
      const isPhone = Math.min(w, h) < 600 && getBrowserDeviceType() !== 'tablet';
      const compact = !desktopBrowser && isLandscape && isPhone;
      setIsCompactMode(compact);

    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, [desktopBrowser]);

  // Toggle body class for compact-mode CSS overrides
  useEffect(() => {
    const cls = 'compact-mode';
    if (isCompactMode) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [isCompactMode]);

  // Detect desktop (large screen with precise pointer)
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false;
    const wide = window.matchMedia('(min-width: 1024px)').matches;
    const pointerFine = window.matchMedia('(pointer: fine)').matches;
    return wide && pointerFine;
  });
  useEffect(() => {
    const computeDesktop = () => {
      const wide = window.matchMedia('(min-width: 1024px)').matches;
      const pointerFine = window.matchMedia('(pointer: fine)').matches;
      setIsDesktop(wide && pointerFine);
    };
    computeDesktop();
    window.addEventListener('resize', computeDesktop);
    const mq = window.matchMedia('(min-width: 1024px)');
    mq.addEventListener?.('change', computeDesktop);
    return () => {
      window.removeEventListener('resize', computeDesktop);
      mq.removeEventListener?.('change', computeDesktop);
    };
  }, []);

  // Toggle a body class for desktop to allow CSS overrides
  useEffect(() => {
    const cls = 'desktop';
    if (isDesktop) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [isDesktop]);

  // Meme mecanique pour la mise en page bureau, qui a ses propres surcharges CSS
  useEffect(() => {
    const cls = 'desktop-browser';
    if (desktopBrowser) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [desktopBrowser]);

  // Close any open drawer when tapping/clicking outside the panel content
  useEffect(() => {
    if (!activePanelId) return;

    const handleClickAway = (event) => {
      const drawerEl = drawerPaperRef.current;
      if (!drawerEl) {
        return;
      }
      if (drawerEl.contains(event.target)) {
        return;
      }
      setActivePanelId(null);
    };

    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('touchstart', handleClickAway);

    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('touchstart', handleClickAway);
    };
  }, [activePanelId]);

  // Gammes posables sur la progression en cours. Les tonalites et modes
  // verrouilles sont ecartes : passer par le manche ne doit pas ouvrir ce que
  // le selecteur de gamme garde ferme.
  const improScaleOptions = useMemo(() => getCompatibleImproScales({
    rootNote,
    mode: currentMode,
    progressionName: currentProgression,
    chordExtension,
    isModeUnlocked,
    isRootNoteUnlocked,
  }), [rootNote, currentMode, currentProgression, chordExtension, isModeUnlocked, isRootNoteUnlocked]);

  const handleImproScalePick = useCallback((scale) => {
    setImproScale(scale);
    if (scale) track('impro_scale_picked', { root_note: scale.root, mode: scale.mode, progression: currentProgression });
  }, [currentProgression]);

  // Desktop-only keyboard shortcuts
  useEffect(() => {
    if (!isDesktop) return;
    const onKeyDown = async (e) => {
      const tag = (e.target && e.target.tagName) || '';
      // Sur navigateur de bureau un bouton clique a la souris ne doit plus avaler
      // les raccourcis ; ailleurs la regle d'origine ne bouge pas.
      const interactiveTarget = isShortcutTargetBlocked(e.target, { relaxed: desktopBrowser, key: e.key });
      if (interactiveTarget || tag === 'INPUT' || tag === 'TEXTAREA' || e.isComposing) return;
      
      // Skip if Ctrl or Cmd is pressed (let global shortcuts handle it)
      if (e.ctrlKey || e.metaKey) return;
      
      const k = e.key.toLowerCase();
      
      // Spacebar - toggle play/stop
      if (k === ' ') {
        if (activeLesson || activeBackingTrack) return;
        e.preventDefault();

        // If not playing and no progression selected, select default
        if (!isProgressionPlaying && !currentProgression) {
          const progressions = getCompatibleProgressions(currentMode);
          if (progressions.length > 0) {
            setCurrentProgression(progressions[0]);
          }
        }
        
        // Toggle play/stop
        if (notesGridRef.current && notesGridRef.current.handlePlayButtonClick) {
          notesGridRef.current.handlePlayButtonClick();
        }
      }
      // M - toggle metronome
      else if (k === 'm') { 
        e.preventDefault();
        setMetronomeEnabled(prev => !prev);
      }
      // R - open root note drawer
      else if (k === 'r') { 
        e.preventDefault();
        setActivePanelId(activePanelId === 'rootNote' ? null : 'rootNote'); 
      }
      // P - patterns panel
      else if (k === 'p') { 
        e.preventDefault();
        setActivePanelId(activePanelId === 'patterns' ? null : 'patterns'); 
      }
      // Comma - settings page
      else if (k === ',') {
        e.preventDefault();
        setCurrentPage(currentPage === 'settings' ? 'home' : 'settings');
      }
      // C / X - next / previous compatible scale
      else if (k === 'c' || k === 'x') {
        if (e.altKey || e.repeat) return;
        if (activeLesson || activeBackingTrack) return;
        if (!currentProgression || improScaleOptions.length === 0) return;
        e.preventDefault();
        handleImproScalePick(nextImproScale(improScaleOptions, improScale, k === 'c' ? 1 : -1));
      }
      // Escape - close panels
      else if (k === 'escape') {
        setActivePanelId(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDesktop, desktopBrowser, isProgressionPlaying, currentProgression, currentScaleType, currentMode, activePanelId, currentPage, activeLesson, activeBackingTrack, improScaleOptions, improScale, handleImproScalePick]);

  // Initialize audio on mount
  useEffect(() => {
    const initAudioOnMount = async () => {
      try {
        // Initialize audio engine (now unified - all platforms use multiNotePlayer)
        setIsPreRendering(true);

        await multiNotePlayer.initialize();
        // Android : les 36 loops mettent 10,7 s a se decoder sur un Galaxy A50,
        // pendant lesquelles aucune note ne sort. On charge d'abord celles que la
        // gamme affichee peut declencher, le reste se remplit derriere. iOS et le
        // web gardent le prechargement complet, inchange.
        if (isAndroid()) {
          await multiNotePlayer.preloadScaleFirst(
            getNotesInMode(rootNote, currentMode),
            (progress) => setPreRenderProgress(progress)
          );
        } else {
          await multiNotePlayer.preloadAllNotes((progress, completed, total) => {
            setPreRenderProgress(progress);
          });
        }

        // Initialize synthPlayer for metronome
        await audioEngine.initialize();

        // Initialize native audio session guard (iOS: reclaims audio after interruptions)
        await initAudioSessionGuard();

        setIsPreRendering(false);
        setInitialized(true);
      } catch (error) {
        console.error('[App] Failed to initialize audio:', error);
        setIsPreRendering(false);
      }
    };

    // Initialize immediately on mount
    initAudioOnMount();
    // Volontairement au montage seul : rootNote et currentMode ne servent qu'a
    // choisir l'ordre de chargement des loops, la gamme affichee au demarrage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Analytics: track page/screen views
  useEffect(() => {
    track('screen_viewed', { screen: currentPage });
  }, [currentPage]);

  // The Learn page stays mounted (display: none), so its track previews keep
  // playing when the user navigates away. Stop them on page change.
  useEffect(() => {
    if (currentPage !== 'learn') {
      document.dispatchEvent(new CustomEvent('backingtrack:stopall'));
    }
  }, [currentPage]);

  // Load saved user progressions from the user mirror and merge into CHORD_PROGRESSIONS
  useEffect(() => {
    try {
      const userProgressions = getUserProgressions();
      Object.values(userProgressions).forEach((p) => {
        if (p && p.name && Array.isArray(p.degrees)) {
          CHORD_PROGRESSIONS[p.name] = p;
        }
      });
    } catch (e) {
      console.warn('[App] Failed to load user progressions', e);
    }
  }, []);
  
  // Update the audioEngine's current scale when rootNote or currentMode changes
  useEffect(() => {
    // Set the current scale in the audioEngine so it can generate chords correctly
    const updateScale = async () => {
      setIsPreRendering(true);
      setPreRenderProgress(0);

      try {
        await audioEngine.setCurrentScale(rootNote, currentMode, (progress, completed, total) => {
          setPreRenderProgress(progress);
        });
      } catch (error) {
        console.error('Error setting current scale:', error);
      } finally {
        setIsPreRendering(false);
        setPreRenderProgress(100);
      }
    };

    updateScale();
  }, [rootNote, currentMode]);

  // Update the audioEngine's high octave setting when preference changes.
  useEffect(() => {
    audioEngine.setPlayHighOctave(playHighOctave);
  }, [playHighOctave]);

  useEffect(() => {
    Tone.getDestination().volume.value = 0;
  }, []);

  // Map the bass mode onto the audioEngine (drone on/off + static vs moving).
  useEffect(() => {
    audioEngine.setDrone(bassMode !== 'none');
    audioEngine.setStaticBass(bassMode === 'static');
  }, [bassMode]);

  // Beat state intentionally NOT mirrored into App root: the beat indicator is
  // rendered by NoteGrid (its own useSynthPlayer instance). Keeping it out of the
  // root avoids a full-tree re-render on every beat during playback.

  // Update metronome settings in synthPlayer when they change. Persistence is
  // handled by the user mirror via useUserSetting.
  useEffect(() => {
    if (synthPlayer?.setBPM && initialized) {
      synthPlayer.setBPM(metronomeBPM);
    }
  }, [metronomeBPM, initialized, synthPlayer?.setBPM]);

  useEffect(() => {
    if (synthPlayer?.setTimeSignature && initialized) {
      synthPlayer.setTimeSignature(metronomeTimeSignature);
    }
  }, [metronomeTimeSignature, initialized, synthPlayer?.setTimeSignature]);

  useEffect(() => {
    if (synthPlayer?.setMetronomeVolume && initialized) {
      synthPlayer.setMetronomeVolume(metronomeVolume);
    }
  }, [metronomeVolume, initialized, synthPlayer?.setMetronomeVolume]);

  useEffect(() => {
    if (synthPlayer?.setMetronomeVisualOnly && initialized) {
      synthPlayer.setMetronomeVisualOnly(metronomeVisualOnly);
    }
  }, [metronomeVisualOnly, initialized, synthPlayer?.setMetronomeVisualOnly]);

  // Handle metronome enable/disable - DISABLED: SynthPlayer now manages metronome
  useEffect(() => {
    if (!initialized) return;

    // SynthPlayer now handles all metronome timing - disable old automatic management
    
    // Old metronome engine removed - synthPlayer handles everything now
  }, [metronomeEnabled, initialized, isProgressionPlaying]);

  // Handle metronome when progression stops - DISABLED: SynthPlayer now manages metronome
  useEffect(() => {
    if (!initialized) return;
    
    // SynthPlayer handles all metronome lifecycle - old progression stop management disabled
    
    // Old metronome engine removed - synthPlayer handles everything now
  }, [isProgressionPlaying, initialized, metronomeEnabled]);

  // Initialize audio engine on first user interaction (fallback for web)
  const initializeAudio = async () => {
    if (initialized) {
      return;
    }

    try {
      // Initialize audio engine (unified - all platforms use multiNotePlayer)
      await audioEngine.initialize();
      setInitialized(true);
    } catch (error) {
      console.error("[App] Error initializing audio:", error);
    }
  };


  const handleRootNoteChange = (newRootNote) => {
    // Stop any currently playing sounds
    audioEngine.stopAllSounds();
    publishPlayChord({ playingNote: null, activeChordNote: null, displayedChordNote: null });

    // Update the root note
    setRootNote(newRootNote);
    track('root_note_changed', { root_note: newRootNote });
  };

  const handleScaleTypeChange = (newScaleType, tier = null) => {
    // Stop any currently playing sounds
    audioEngine.stopAllSounds();
    publishPlayChord({ playingNote: null, activeChordNote: null, displayedChordNote: null });

    setCurrentScaleType(newScaleType);
    // Pick the first mode within the tier if provided, else the first mode of the scale type
    const modesInTier = tier ? getModesForScaleTypeInTier(newScaleType, tier) : [];
    const firstModeOfScale = modesInTier[0] || MODE_NAMES_BY_SCALE[newScaleType][0];
    setCurrentMode(firstModeOfScale);
    // The mood label only describes a mood the user picked; a direct scale change clears it
    setCurrentMood(null);
    // Reset to the new mode's default progression so it always matches the scale
    setCurrentProgression(getDefaultProgression(firstModeOfScale)?.name || null);
    track('scale_type_changed', { scale_type: newScaleType, first_mode: firstModeOfScale, tier });
  };

  const handleModeChange = (newMode) => {
    // Stop any currently playing sounds
    audioEngine.stopAllSounds();
    publishPlayChord({ playingNote: null, activeChordNote: null, displayedChordNote: null });

    setCurrentMode(newMode);
    track('mode_changed', { mode: newMode });

    // The mood label only describes a mood the user picked; a direct mode change clears it
    setCurrentMood(null);
    // Reset to the new mode's default progression so it always matches the scale
    setCurrentProgression(getDefaultProgression(newMode)?.name || null);
  };

  const handleMoodChange = (newMood) => {
    // Stop any currently playing sounds
    audioEngine.stopAllSounds();
    publishPlayChord({ playingNote: null, activeChordNote: null, displayedChordNote: null });
    
    setCurrentMood(newMood);
    track('mood_changed', { mood: newMood });

    // Find the corresponding scale for this mood
    const moodMapping = MOOD_TO_SCALE.find(item => item.mood === newMood);
    if (moodMapping) {
      // Determine which scale type this mode belongs to
      let foundScaleType = null;
      
      // Search through all scale types to find which one contains this mode
      for (const scaleType in MODE_NAMES_BY_SCALE) {
        if (MODE_NAMES_BY_SCALE[scaleType].includes(moodMapping.scale)) {
          foundScaleType = scaleType;
          break;
        }
      }
      
      // Update scale type if found
      if (foundScaleType) {
        setCurrentScaleType(foundScaleType);
      }
      
      // Update the mode
      setCurrentMode(moodMapping.scale);
      // Keep the progression matched to the new scale
      setCurrentProgression(getDefaultProgression(moodMapping.scale)?.name || null);
    }
  };


  // Function to handle note played from NoteGrid
  const handleNotePlay = async (note) => {
    // Stop backing track if playing
    if (backingTrackAudioRef.current) {
      backingTrackAudioRef.current.pause();
    }
    // Exit jam mode if active
    if (activeBackingTrack) {
      setActiveBackingTrack(null);
      setActiveBackingTrackArtist(null);
    }

    let startedByRecording = false;

    // If armed, auto-start recording on first chord
    if (isRecordArmed && !isRecording) {
      // DISABLED: SynthPlayer now handles metronome timing
      // Ensure metronome is enabled and start it in sync
      setMetronomeEnabled(true);
      recordStartTimeRef.current = Tone.getTransport().now();
      startedByRecording = false; // Don't flag as started by recording since we're not using old metronome
      setIsRecording(true);
      setIsRecordArmed(false);
      recordedEventsRef.current = [];

      // Immediately capture the first chord at the metronome start time so it quantizes to bar 1
      recordedEventsRef.current.push({
        note,
        time: recordStartTimeRef.current,
      });
    }

    // La note qui sonne, l'accord actif et l'accord affiche pour le motif
    publishPlayChord({ playingNote: note, activeChordNote: note, displayedChordNote: note });

    track('chord_played', { note, root_note: rootNote, mode: currentMode });
    
    // DISABLED: SynthPlayer now handles metronome timing
    // Start metronome if enabled and not already playing (skip if we already started it for recording)

    // If currently recording, capture the event (timestamping/quantization to be implemented next)
    if (isRecording) {
      const eventTime = Tone.getTransport().now();
      recordedEventsRef.current.push({
        note,
        time: eventTime,
      });
    }
  };
  
  
  // Function to stop all sounds
  const handleStopAllSounds = () => {

    // Stop sounds from audio engine
    audioEngine.stopAllSounds();
    // Stop metronome if it's playing but keep the toggle state
    if (synthPlayer) {
      synthPlayer.stop();
    }

    // Stop backing track if playing
    if (backingTrackAudioRef.current) {
      backingTrackAudioRef.current.pause();
    }

    // Stop any backing track preview playing in the Learn page cards
    document.dispatchEvent(new CustomEvent('backingtrack:stopall'));

    // Clear the playing note only. activeChordNote and displayedChordNote stay
    // for pattern display and are cleared when the progression stops: this
    // prevents losing the active chord highlighting when switching chords.
    publishPlayChord({ playingNote: null });
  };

  // Sortie de lecon. restoreScale = false quand c'est un choix de gamme dans le
  // tiroir qui fait sortir : restaurer annulerait ce que l'utilisateur vient de
  // choisir. Chevron retour et bouton Android restaurent.
  const handleExitLesson = ({ restoreScale = true } = {}) => {
    const restore = lessonRestoreRef.current;
    lessonRestoreRef.current = null;
    setActiveLesson(null);
    setActiveLessonArtist(null);
    setIsLessonPlaying(false);
    if (restoreScale && restore?.scale) {
      setRootNote(restore.scale.rootNote);
      setCurrentMode(restore.scale.mode);
      setCurrentProgression(restore.scale.progression);
      setCurrentMood(restore.scale.mood);
    }
  };

  const handleLeaveLessonToJam = () => {
    const packId = activeLesson?.packId || null;
    handleExitLesson();
    if (packId) setLessonReturnPackId(packId);
    setCurrentPage('learn');
  };

  const clearLessonReturnPack = useCallback(() => setLessonReturnPackId(null), []);

  // Les deux onglets de gauche pendant une lecon. Une lecon s'affiche dans la
  // page Play mais se choisit dans Practice : Play la referme et rend le manche
  // dans l'etat d'avant, Practice la referme et rend la page d'ou elle vient.
  // Les deux restaurent la gamme, comme le chevron de retour et le bouton
  // Android : seul un choix de gamme dans le tiroir sort sans restaurer.
  const goToPlay = () => {
    if (activeLesson) handleExitLesson();
    setCurrentPage('home');
    setTabValue(0);
  };

  const goToPractice = () => {
    if (activeLesson) {
      handleExitLesson();
      setLessonReturnPackId(null);
      learnPageRef.current?.goHome?.();
    }
    setCurrentPage('learn');
    setTabValue(1);
  };

  // Entree dans une lecon depuis Jam. Les droits sont ceux des backings : une
  // lecon verrouillee ne s'ouvre pas (la carte a deja propose l'achat du pack).
  const handleEnterLesson = (lesson, artist) => {
    if (!lesson || !isTrackUnlocked(lesson, purchasedPacks)) return;
    handleStopAllSounds();
    publishPlayChord({ displayedChordNote: null, activeChordNote: null });
    if (activeBackingTrack) handleExitJamMode();
    // Une lecon ouverte par-dessus une autre garde le premier instantane.
    if (!lessonRestoreRef.current) lessonRestoreRef.current = { scale: null };
    const restore = lessonRestoreRef.current;
    if (!lesson.transposable && lesson.rootNote && lesson.mode) {
      if (!restore.scale) {
        restore.scale = { rootNote, mode: currentMode, progression: currentProgression, mood: currentMood };
      }
      setRootNote(lesson.rootNote);
      setCurrentMode(lesson.mode);
      setCurrentProgression(getDefaultProgression(lesson.mode)?.name || null);
      setCurrentMood(null);
    }
    setIsLessonPlaying(false);
    setActiveLesson(lesson);
    setActiveLessonArtist(artist);
    track('lesson_started', { lesson_id: lesson.id, artist: artist?.name });
    setCurrentPage('home');
  };

  const applyBackingScale = (newRoot, newMode) => {
    setRootNote(newRoot);
    setCurrentMode(newMode);
    setCurrentMood(null);
    setCurrentProgression(getDefaultProgression(newMode)?.name || null);
  };

  // Enter jam mode with a backing track
  const handleEnterJamMode = (backingTrack, artist, jamScale) => {
    handleStopAllSounds(); // Stop chord drones and progressions
    // Clear so MIDI sync can drive the fretboard
    publishPlayChord({ displayedChordNote: null, activeChordNote: null });
    if (activeLesson) handleExitLesson({ restoreScale: false });
    // Le player garde la meme instance d'une piste a l'autre : il oublie sa
    // section au changement de track.id, App doit oublier la sienne en meme temps.
    if (activeBackingTrack?.id !== backingTrack.id) setBackingSection(null);
    setActiveBackingTrack(backingTrack);
    setActiveBackingTrackArtist(artist);
    // Un rung different du mode de base devient un verrou de type dans le player ;
    // le mode de base lui-meme = suivre les marqueurs, pas de verrou
    const lockedType = jamScale && jamScale !== backingTrack.mode ? jamScale : null;
    setActiveBackingTrackJamScale(lockedType);
    setBackingLockChecked(false);
    track('backing_track_started', { track_name: backingTrack?.name, artist });
    // An explicit jamScale (a difficulty-ladder rung) always goes through the
    // section rule, even when the current scale is already compatible: before the
    // first marker, the track's own key stands in for the section.
    if (jamScale) {
      const armed = resolveBackingScale({
        type: lockedType,
        rootNote: backingTrack.rootNote || rootNote,
        mode: backingTrack.mode || jamScale,
        isModeUnlocked,
        checked: false,
      });
      applyBackingScale(armed.root, armed.mode);
    } else {
      // Only sync scale if current mode is not compatible with this backing track
      const isAlreadyCompatible = backingTrack.compatibleScales &&
        backingTrack.compatibleScales.includes(currentMode) &&
        rootNote === backingTrack.rootNote;
      if (!isAlreadyCompatible) {
        if (backingTrack.rootNote) {
          setRootNote(backingTrack.rootNote);
        }
        if (backingTrack.mode) {
          setCurrentMode(backingTrack.mode);
          // Match the progression to the jam scale
          setCurrentProgression(getDefaultProgression(backingTrack.mode)?.name || null);
        }
        // Jumping into a jam scale isn't a mood pick, so drop any stale mood label
        setCurrentMood(null);
      }
    }
    setCurrentPage('home'); // Switch to Play screen
  };

  // Coach: tapping the pulsing Jam button skips the browse screen and drops the
  // user straight into the best unlocked backing track (relevance order, so the
  // A Ionian default lands on the perfect match). Falls back to the Jam page.
  const launchFreeJam = () => {
    onCoachJamTapped();
    track('onboarding_jam_tapped');
    const jamEntry = getAllBackingTracksSorted(currentMode, rootNote)
      .find((entry) => entry.track.audioUrl && isTrackUnlocked(entry.track, purchasedPacks));
    if (jamEntry) {
      handleEnterJamMode(jamEntry.track, jamEntry.artist);
    } else {
      setCurrentPage('learn');
      setTabValue(1);
    }
  };

  // Exit jam mode
  const handleExitJamMode = () => {
    if (backingTrackAudioRef.current) {
      backingTrackAudioRef.current.pause();
    }
    setActiveBackingTrack(null);
    setActiveBackingTrackArtist(null);
    setActiveBackingTrackJamScale(null);
    setBackingLockChecked(false);
    setBackingSection(null);
    setBackingScalesOpen(false);
    setIsBackingTrackPlaying(false);
  };

  // Bouton retour Android : une pression ferme la couche visible la plus haute,
  // de la plus récente à la plus ancienne. Les snackbars ne comptent pas, elles
  // se ferment seules. Quand plus rien n'est ouvert et qu'on est sur l'accueil,
  // rendre false laisse le hook quitter l'app.
  useAndroidBackButton(() => {
    if (savePatternDialogOpen) { setSavePatternDialogOpen(false); return true; }
    if (signUpOpen) { setSignUpOpen(false); return true; }
    if (unlockSheetContext) { closeUnlockSheet(); return true; }
    if (improScalesOpen) { setImproScalesOpen(false); return true; }
    if (backingScalesOpen) { setBackingScalesOpen(false); return true; }
    if (activePanelId !== null) { setActivePanelId(null); return true; }
    if (activeLesson) {
      if (lessonPlayerRef.current?.handleBack?.()) return true;
      handleLeaveLessonToJam();
      return true;
    }
    if (activeBackingTrack) { handleExitJamMode(); return true; }
    if (currentPage === 'settings' && youPageRef.current?.handleBack?.()) return true;
    if (currentPage !== 'home') { setCurrentPage('home'); return true; }
    return false;
  });
  
  // Function to handle random selection
  const handleRandomSelection = () => {

    // Get random root note (free tier: natural keys only)
    const rootPool = ALL_NOTES.filter(isRootNoteUnlocked);
    const randomRoot = rootPool[Math.floor(Math.random() * rootPool.length)];

    // Weighted tier pick (essentials 70% / colors 25% / wild 5%) per PLAYGROUND v1
    // Free tier: the pool shrinks to the unlocked modes of the picked tier, and
    // falls back to any unlocked mode so Surprise me never lands on a locked scale.
    const r = Math.random();
    const tierPick = r < 0.7 ? 'essentials' : (r < 0.95 ? 'colors' : 'wild');
    const modePool = (MODES_BY_TIER[tierPick] || []).filter(isModeUnlocked);
    const fallbackPool = Object.values(MODES_BY_TIER).flat().filter(isModeUnlocked);
    const pool = modePool.length ? modePool : fallbackPool;
    const randomMode = pool.length
      ? pool[Math.floor(Math.random() * pool.length)]
      : MODE_NAMES_BY_SCALE[SCALE_TYPES[0]][0];

    // Derive scale type from picked mode
    let randomScaleType = SCALE_TYPES[0];
    for (const st of SCALE_TYPES) {
      if ((MODE_NAMES_BY_SCALE[st] || []).includes(randomMode)) {
        randomScaleType = st;
        break;
      }
    }

    // Get compatible progression names for the random mode
    const progressionNames = getCompatibleProgressions(randomMode);
    const randomProgression = progressionNames[Math.floor(Math.random() * progressionNames.length)];
    
    // Get the mood for the random mode
    const moodsForMode = getMoodsForScale(randomMode);
    const randomMood = moodsForMode.length > 0 ? moodsForMode[0] : null;

    // Apply the random selections
    setRootNote(randomRoot);
    setCurrentScaleType(randomScaleType);
    setCurrentMode(randomMode);
    setCurrentProgression(randomProgression);
    if (randomMood) {
      setCurrentMood(randomMood);
    }
    track('random_selection', { root_note: randomRoot, scale_type: randomScaleType, mode: randomMode, progression: randomProgression, tier: tierPick });
  };

  // Metronome control functions
  const handleMetronomeToggle = () => {
    track('metronome_toggled', { enabled: !metronomeEnabled, bpm: metronomeBPM });
    setMetronomeEnabled(!metronomeEnabled);
  };

  const handleMetronomeBPMChange = (bpm) => {
    setMetronomeBPM(bpm);
  };

  const handleMetronomeTimeSignatureChange = (timeSignature) => {
    setMetronomeTimeSignature(timeSignature);
  };

  const handleMetronomeVolumeChange = (volume) => {
    setMetronomeVolume(volume);
  };

  const handleMetronomeVisualOnlyChange = (visualOnly) => {
    setMetronomeVisualOnly(visualOnly);
  };

  const handleBarsPerChordChange = (bars) => {
    setBarsPerChord(bars);
  };

  const handleTapTempo = () => {
    if (initialized && synthPlayer) {
      const newBPM = synthPlayer.tap();
      // The tap function returns the current BPM
      setMetronomeBPM(newBPM);
    }
  };

  const handleStartMetronome = () => {
    // DISABLED: SynthPlayer now handles metronome timing
  };

  // Record button handler: arm, disarm, or stop recording
  const handleRecordButtonClick = () => {
    if (!isRecordArmed && !isRecording) {
      setIsRecordArmed(true);
      recordedEventsRef.current = [];
      recordStartTimeRef.current = null;
      return;
    }
    if (isRecordArmed) {
      setIsRecordArmed(false);
      return;
    }
    if (isRecording) {
      setIsRecording(false);
      // Reset metronome enabled state to ensure next recording cycle works
      setMetronomeEnabled(false);
      // Stop all sounds and clear playing state when recording stops
      handleStopAllSounds();
      // Stop metronome if not currently playing a progression
      if (!isProgressionPlaying) {
        if (synthPlayer) {
      synthPlayer.stop();
    }
      }
      // Quantize recorded events to bars and save as a User Progression
      try {
        const events = recordedEventsRef.current || [];
        const startTime = recordStartTimeRef.current ?? (events.length ? events[0].time : Tone.getTransport().now());
        const { bpm, beatsPerBar, timeSignature } = synthPlayer?.state || { bpm: metronomeBPM, beatsPerBar: 4, timeSignature: metronomeTimeSignature };
        const denominator = Number((timeSignature || '4/4').split('/')[1]) || 4;
        const secondsPerQuarter = 60 / (bpm || 120);
        const secondsPerBeat = secondsPerQuarter * (4 / denominator);
        const secondsPerBar = secondsPerBeat * (beatsPerBar || 4);

        if (events.length > 0) {
          const notesInScale = getNotesInMode(rootNote, currentMode);
          const sorted = [...events].sort((a, b) => a.time - b.time);
          
          // Convert notes to degrees and preserve timing
          const chordTimings = sorted.map((ev, index) => {
            const noteIndex = notesInScale.indexOf(ev.note);
            if (noteIndex < 0) return null;
            
            // Calculate timing relative to start, with first chord snapped to 0
            const timing = index === 0 ? 0 : Math.max(0, ev.time - startTime);
            
            return {
              degree: noteIndex + 1, // convert to 1-based degrees
              timing: timing // time in seconds from start
            };
          }).filter(Boolean);

          if (chordTimings.length > 0) {
            // Calculate loop duration to match exactly the elapsed metronome time
            const recordingEndTime = Tone.getTransport().now();
            const actualRecordingDuration = recordingEndTime - startTime;
            
            // Calculate how many complete ticks/bars elapsed during recording
            const elapsedBeats = actualRecordingDuration / secondsPerBeat;
            const elapsedBars = actualRecordingDuration / secondsPerBar;
            
            // Use the actual recording duration, don't round up
            const loopDuration = actualRecordingDuration;
            const recordedBars = elapsedBars;
            
            
            // Generate a unique user progression name
            let i = 1;
            let name;
            do {
              name = `User Progression ${i}`;
              i += 1;
            } while (CHORD_PROGRESSIONS[name]);

            const prog = {
              name,
              degrees: chordTimings.map(ct => ct.degree),
              timings: chordTimings.map(ct => ct.timing),
              loopDuration: loopDuration,
              recordedBars: recordedBars,
              description: 'Recorded progression',
              isRecorded: true
            };

            // Merge into in-memory catalog
            CHORD_PROGRESSIONS[name] = prog;

            // Persist to the user mirror (write-through IndexedDB)
            try {
              saveUserProgression(name, prog);
            } catch (e) {
              console.warn('[App] Failed to persist user progression', e);
            }

            // Set as active progression
            setCurrentProgression(name);
          }
        }
      } catch (e) {
        console.warn('[App] Failed to process recorded progression', e);
      } finally {
        recordedEventsRef.current = [];
        recordStartTimeRef.current = null;
      }
      return;
    }
  };


  // Function to handle chord progression change
  const handleProgressionChange = (newProgression) => {
    // "Random" now means "Best Fit for Scale" - handled in NoteGrid component
    setCurrentProgression(newProgression);
    track('progression_changed', { progression: newProgression, root_note: rootNote, mode: currentMode });
  };

  // Changer de tonalite, de gamme ou de progression remet le manche sur la
  // gamme harmonique : la gamme d'impro precedente ne repond plus a la question
  // posee. Le mode jam a son propre verrou, celui-ci s'efface devant lui.
  useEffect(() => {
    setImproScale(null);
    setImproScalesOpen(false);
  }, [rootNote, currentMode, currentProgression, activeBackingTrack, activeLesson]);

  // Pendant un backing, la pilule regle le verrou du player et non improScale :
  // le reset ci-dessus l'effacerait a chaque section. Meme regle que le player
  // (resolveBackingScale), sur la section en cours ou la gamme de la piste avant
  // le premier marqueur. Le dialogue reste ouvert d'une section a l'autre.
  const backingScale = useMemo(() => {
    if (!activeBackingTrack) return null;
    const sectionRoot = backingSection?.root || activeBackingTrack.rootNote;
    const sectionScale = backingSection?.scale || activeBackingTrack.mode;
    const resolved = resolveBackingScale({
      type: activeBackingTrackJamScale,
      rootNote: sectionRoot,
      mode: sectionScale,
      isModeUnlocked,
      checked: backingLockChecked,
    });
    const lockedOption = resolved.options.find((o) => o.mode === activeBackingTrackJamScale);
    // Un verrou de l'auteur hors des options ne surligne rien : la base n'est
    // pas ce qui s'allume, elle reste cliquable pour le retirer.
    const heldCurated = !backingLockChecked && !!activeBackingTrackJamScale && activeBackingTrackJamScale !== sectionScale;
    return {
      ...resolved,
      sectionRoot,
      sectionScale,
      sectionName: backingSection?.name || null,
      dialogScale: lockedOption || (heldCurated ? { root: sectionRoot, mode: activeBackingTrackJamScale } : null),
    };
  }, [activeBackingTrack, backingSection, activeBackingTrackJamScale, backingLockChecked, isModeUnlocked]);

  const handleBackingScalePick = (scale) => {
    const type = scale ? scale.mode : null;
    setActiveBackingTrackJamScale(type);
    setBackingLockChecked(true);
    const next = resolveBackingScale({
      type,
      rootNote: backingScale.sectionRoot,
      mode: backingScale.sectionScale,
      isModeUnlocked,
    });
    applyBackingScale(next.root, next.mode);
    if (type) track('impro_scale_picked', { root_note: next.root, mode: type, backing_track: activeBackingTrack?.id });
  };

  // Random chords: select the sentinel progression and start infinite random
  // playback immediately (chords drawn from the current scale, no repeats in a row).
  const handleRandomChords = () => {
    setCurrentProgression(RANDOM_PROGRESSION);
    setActivePanelId(null);
    if (notesGridRef.current && notesGridRef.current.playRandom) {
      notesGridRef.current.playRandom();
    }
  };
  
  // State for selected notes on the fretboard
  const [selectedNotes, setSelectedNotes] = useState([]);
  
  // Paint mode state for pattern management
  const [isPaintMode, setIsPaintMode] = useState(false);

  // Ref that always holds the latest app state snapshot for the keyboard logger
  const appStateSnapshotRef = useRef({});
  useEffect(() => {
    appStateSnapshotRef.current = {
      rootNote, currentMode, currentScaleType, currentMood,
      selectedNotes, isPaintMode,
      currentPage, tabValue, activePanelId, isDesktop, isCompactMode,
      savePatternDialogOpen, showPatternSnackbar,
      playHighOctave, initialized,
      metronomeEnabled, metronomeBPM, metronomeTimeSignature, metronomeVolume,
      metronomeVisualOnly,
      currentProgression, isProgressionPlaying, barsPerChord,
      isRecordArmed, isRecording
    };
  });

  // Raccourcis du navigateur de bureau. Tout se lit dans une ref : l'effet ne se
  // reabonne jamais, et chaque touche voit l'etat du dernier rendu.
  const shortcutStateRef = useRef({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    shortcutStateRef.current = {
      currentPage, rootNote, activeLesson, activeBackingTrack, coachStep,
      isRootNoteUnlocked, setUnlockSheetContext, handleRootNoteChange,
      handleChordExtensionToggle, handleRandomSelection, launchFreeJam,
      setCurrentPage, setTabValue, setActivePanelId,
      goToPlay, goToPractice,
    };
  });

  useEffect(() => {
    if (!desktopBrowser) return;
    const onKeyDown = (e) => {
      const action = resolveDesktopShortcut(e);
      if (!action) return;
      if (isShortcutTargetBlocked(e.target, { relaxed: true, key: e.key })) return;
      // Une modale ouverte (tiroir, feuille de deblocage) garde la main. Seule la
      // popup des raccourcis reste pilotable, elle n'est pas une modale.
      if (action.type !== 'shortcuts' && document.querySelector('.MuiModal-root:not(.MuiModal-hidden)')) return;

      const s = shortcutStateRef.current;
      const inPlay = s.currentPage === 'home' && !s.activeLesson && !s.activeBackingTrack;

      switch (action.type) {
        case 'degree':
          if (!inPlay || !notesGridRef.current?.playDegree) return;
          e.preventDefault();
          notesGridRef.current.playDegree(action.index);
          return;
        case 'keyStep': {
          if (!inPlay) return;
          const index = resolveAllNotesIndex(s.rootNote);
          if (index === -1) return;
          e.preventDefault();
          const next = ALL_NOTES[(index + action.delta + ALL_NOTES.length) % ALL_NOTES.length];
          // Meme chemin que la tuile du tiroir : une tonalite payante ouvre la
          // feuille de deblocage au lieu de changer la root.
          if (!s.isRootNoteUnlocked(next)) {
            s.setUnlockSheetContext({ type: 'root', note: next });
            return;
          }
          s.handleRootNoteChange(next);
          return;
        }
        case 'neckView':
          if (s.currentPage !== 'home' || s.activeLesson) return;
          e.preventDefault();
          window.dispatchEvent(new CustomEvent(TOGGLE_NECK_VIEW_EVENT));
          return;
        case 'chordExtension':
          if (!inPlay) return;
          e.preventDefault();
          s.handleChordExtensionToggle();
          return;
        case 'random':
          if (!inPlay) return;
          e.preventDefault();
          s.handleRandomSelection();
          return;
        case 'mixer':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent(TOGGLE_MIXER_EVENT));
          return;
        case 'shortcuts':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent(TOGGLE_SHORTCUTS_EVENT));
          return;
        case 'page':
          e.preventDefault();
          if (action.page === 'home') {
            s.goToPlay();
          } else if (action.page === 'learn') {
            if (s.coachStep === 'tapJam') {
              s.launchFreeJam();
              return;
            }
            s.goToPractice();
          } else {
            s.setCurrentPage(s.currentPage === 'settings' ? 'home' : 'settings');
            s.setActivePanelId(null);
          }
          return;
        default:
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [desktopBrowser]);

  // Global keyboard shortcut for logging (works on all platforms)
  useEffect(() => {
    const onKeyDown = (e) => {
      // Check for Alt+C - Clear console
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        console.clear();
        return;
      }

      // Check for Ctrl+0 or Cmd+0 (for Mac) - Log app state
      if ((e.ctrlKey || e.metaKey) && e.key === '1') {
        e.preventDefault();

        // Les trois etats de l'accord ne passent plus par le rendu de App :
        // on les lit au magasin au moment de la frappe.
        const s = { ...appStateSnapshotRef.current, ...readPlayChord() };
        const appState = {
          ...s,
          recordStartTime: recordStartTimeRef.current,
          notesInMode: getNotesInMode(s.rootNote, s.currentMode),
          progressionData: s.currentProgression ? (() => {
            const { getProgressionByName, getProgressionChords } = require('./utils/chordProgressions');
            const progression = getProgressionByName(s.currentProgression);
            const notesInMode = getNotesInMode(s.rootNote, s.currentMode);
            const chords = getProgressionChords(s.currentProgression, notesInMode);
            return { progression, chords, degrees: progression?.degrees };
          })() : null,
        };

        logService.logAppState(appState);
        return;
      }

      // Check for 'F' key - Log fretboard visual state (debug, dev only).
      // Jamais en prod : ce raccourci global avalait la lettre F dans les champs
      // texte (ex. un code beta contenant un F, comme FRIENDS).
      if (process.env.NODE_ENV !== 'production' && e.key.toLowerCase() === 'f') {
        const tag = (e.target && e.target.tagName) || '';
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('triggerFretboardLogging', {
          detail: { timestamp: new Date().toISOString(), source: 'keyboard-shortcut' }
        }));
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []); // Stable: reads from appStateSnapshotRef

  // Function to handle opening the save pattern dialog
  const handleOpenSavePatternDialog = () => {    
    
    setSavePatternDialogOpen(true);    
  };

  // Barre du haut : pastilles de gamme a gauche, cible du portail de la barre
  // d'outils a droite. Sur navigateur de bureau elle est rendue dans le wrapper
  // auto-scale, collee au manche et a sa largeur ; ailleurs elle reste en tete
  // de .main-app-content, sur toute la largeur de la fenetre.
  const renderHeaderRow = () => (
    <div className="header-row" style={{ flexShrink: 0 }}>
      {/* Header Pills - Scale & Progression selection */}
      <HeaderPills
        rootNote={rootNote}
        currentMode={currentMode}
        currentMood={currentMood}
        onScalePillClick={() => setActivePanelId('rootNote')}
        showScalePill={!(activeLesson && !activeLesson.transposable)}
        showImproScales={!activeLesson && (backingScale
          ? backingScale.options.length > 0 || !!activeBackingTrackJamScale
          : !!currentProgression && improScaleOptions.length > 0)}
        improScaleLabel={backingScale
          ? (activeBackingTrackJamScale ? formatScaleLabel(backingScale.sectionRoot, activeBackingTrackJamScale) : null)
          : (improScale ? formatScaleLabel(improScale.root, improScale.mode) : null)}
        improScalePaused={!!backingScale?.paused}
        onImproScalesClick={() => (backingScale ? setBackingScalesOpen(true) : setImproScalesOpen(true))}
      />

      {/* Portal target for sliding submenu */}
      <div id="sliding-submenu-portal" />
    </div>
  );

  // Onglet allume dans la barre du bas et dans le rail. Une lecon s'affiche
  // dans la page Play, mais elle se choisit dans Practice : c'est Practice qui
  // reste allumee tant qu'elle est ouverte. La page You garde la sienne, on y
  // va sans fermer la lecon.
  const navPage = activeLesson && currentPage === 'home' ? 'learn' : currentPage;

  // Entree de la nav de gauche sur bureau : pave arrondi, teinte quand la page
  // est active. Vide ailleurs, la barre du bas garde son rendu.
  const railItemSx = (active) => (desktopBrowser ? {
    width: 64,
    padding: '10px 0',
    borderRadius: '14px',
    backgroundColor: active ? 'rgba(var(--accent-rgb), 0.14)' : 'transparent',
    transition: 'background-color 0.15s ease',
    '&:hover': { backgroundColor: active ? 'rgba(var(--accent-rgb), 0.18)' : 'rgba(255, 255, 255, 0.06)' },
    '& .MuiSvgIcon-root': { fontSize: 28 },
    '& .MuiTypography-root': { fontSize: '0.75rem', marginTop: '4px' }
  } : {});

  // Sur bureau la barre prend la largeur du manche et vit juste au-dessus de lui
  const renderScaledHeaderRow = () => (
    <Box sx={{ maxWidth: '1470px', mx: 'auto', width: '100%' }}>
      {renderHeaderRow()}
    </Box>
  );

  const renderGuitarNeck = () => (
    <Profiler id="neck" onRender={onRenderProfile}>
    <Box sx={{ pt: '14px', maxWidth: '1470px', mx: 'auto', width: '100%' }} className="guitar-neck-container">
      <PlayNeck
        key="guitar-neck-visualizer"
        active={Boolean(activeLesson)}
        rootNote={improScale?.root || rootNote}
        currentMode={improScale?.mode || currentMode}
        fallbackChordNote={rootNote}
        chordExtension={chordExtension}
        className="guitar-neck-visualizer-container"
        selectedNotes={selectedNotes}
        onSelectedNotesChange={activeLesson ? noopSelectedNotesChange : setSelectedNotes}
        onShowPatternSnackbar={setShowPatternSnackbar}
        currentProgression={currentProgression}
        isProgressionPlaying={synthPlayer?.isPlaying || synthPlayer?.isPaused || isBackingTrackPlaying || isLessonPlaying}
        isPaintMode={isPaintMode}
        onPaintModeChange={setIsPaintMode}
        isCompactMode={isCompactMode}
        coachStep={coachStep}
        discoveryTip={discoveryTip}
        onDiscoveryTipDismiss={dismissDiscoveryTip}
      />
    </Box>
    </Profiler>
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: '#000000',
        paddingLeft: isNative() ? { xs: 0, sm: '44px' } : 0,
        paddingRight: isNative() ? { xs: 0, sm: '44px' } : 0
      }}>
        
        {/* Main content area - no top tabs anymore */}
        <Box className="main-scroll" sx={{
          width: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          position: 'absolute',
          top: isDesktop ? 0 : 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
          left: 0,
          right: 0,
          bottom: 0,
          paddingBottom: '0px',
          // Le rail de gauche prend la place de la barre du bas : le contenu se
          // decale au lieu de reserver du vide sous lui. width auto, sinon
          // left + right + width se contredisent et le bord droit sort du cadre.
          ...(desktopBrowser && {
            left: `${DESKTOP_RAIL_WIDTH}px`,
            width: 'auto'
          })
        }}>
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            p: 0,
            m: 0,
            ml: 0,
            height: '100%',
            // Add safe area padding for iOS in all orientations
            paddingLeft: isNative() ? 'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))' : 0,
            paddingRight: isNative() ? 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))' : 0
          }}>
          {/* Main App Content */}
          <Box
            className="main-app-content"
            sx={{
              display: currentPage === 'home' ? 'flex' : 'none',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
              boxSizing: 'border-box',
              // La barre du bas et le menu radial flottent au-dessus du contenu
              // (position fixed). Sans cette reserve, les dernieres cartes de
              // degres passent dessous sur les petits ecrans.
              paddingBottom: isCompactMode || desktopBrowser
                ? 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))'
                : `calc(${BOTTOM_NAV_RESERVE}px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))`
            }}
          >
            {/* Header row - pills on left, toolbar portal on right in landscape */}
            {!desktopBrowser && renderHeaderRow()}

            {/* Vertically center guitar neck + note grid on tablet landscape / desktop (not HCMODE) */}
            <Box
              className="play-area"
              ref={activeLesson ? undefined : playAreaContainerRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                ...(activeLesson && {
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }),
                // Quand le contenu tient sans reduction, on le centre au lieu de
                // le laisser colle en haut avec du vide sous les degres.
                ...(!activeLesson && ((playAreaScale >= 1 && playAreaFits) || playAreaWidthLimited) && {
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }),
              }}
            >
            {activeLesson && (
              <Box
                ref={playAreaContainerRef}
                sx={{
                  flex: '0 1 auto',
                  width: '100%',
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  ref={playAreaContentRef}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: playAreaScale < 1 ? `${100 / playAreaScale}%` : '100%',
                    transform: `scale(${playAreaScale})`,
                    transformOrigin: 'top left',
                    ...(playAreaScale < 1 && {
                      height: `${100 / playAreaScale}%`,
                    }),
                  }}
                >
                  {desktopBrowser && renderScaledHeaderRow()}
                  {renderGuitarNeck()}
                </Box>
              </Box>
            )}
            {/* Auto-scaled content wrapper */}
            <Box
              ref={activeLesson ? undefined : playAreaContentRef}
              sx={{
                display: activeLesson ? 'none' : 'flex',
                flexDirection: 'column',
                width: playAreaScale < 1 ? `${100 / playAreaScale}%` : '100%',
                transform: `scale(${playAreaScale})`,
                transformOrigin: playAreaWidthLimited ? 'center left' : 'top left',
                ...(playAreaScale < 1 && !playAreaWidthLimited && {
                  height: `${100 / playAreaScale}%`,
                }),
                // Reduit par la largeur seule : la boite garde sa hauteur
                // naturelle et se centre, l'echelle part du milieu.
                ...(playAreaWidthLimited && { flexShrink: 0 }),
              }}
            >

            {desktopBrowser && !activeLesson && renderScaledHeaderRow()}
            {!activeLesson && renderGuitarNeck()}

            <Box sx={{
              mt: 'clamp(4px, 1.5vh, 16px)'
            }}>
              <Profiler id="jam" onRender={onRenderProfile}>
              {!activeLesson && (activeBackingTrack ? (
                <BackingTrackPlayer
                  track={activeBackingTrack}
                  artist={activeBackingTrackArtist}
                  currentMode={currentMode}
                  lockedScaleType={activeBackingTrackJamScale}
                  lockedScaleChecked={backingLockChecked}
                  onLockedScaleTypeChange={(type) => { setActiveBackingTrackJamScale(type); setBackingLockChecked(false); }}
                  onSectionChange={setBackingSection}
                  isModeUnlocked={isModeUnlocked}
                  onScaleChange={applyBackingScale}
                  ref={backingTrackAudioRef}
                  onClose={handleExitJamMode}
                  onPlaybackStart={handleStopAllSounds}
                  onChordChange={publishActiveChordNote}
                  onPlayingStateChange={setIsBackingTrackPlaying}
                  onTempoChange={(bpm) => { setMetronomeBPM(bpm); }}
                  onTimeSignatureChange={(ts) => { setMetronomeTimeSignature(ts); }}
                />
              ) : (
                <PlayNoteGrid
                  ref={notesGridRef}
                  rootNote={rootNote}
                  currentMode={currentMode}
                  onAudioInit={initializeAudio}
                  onPlayNote={handleNotePlay}
                  onStopAllSounds={handleStopAllSounds}
                  currentProgression={currentProgression}
                  onProgressionChange={handleProgressionChange}
                  isProgressionPlaying={isProgressionPlaying}
                  onRootNoteChange={setRootNote}
                  onModeChange={setCurrentMode}
                  onPlayingStateChange={(playing, playbackState) => {
                    setIsProgressionPlaying(playing);
                    setIsProgressionPaused(playbackState === 'paused');
                    // Clear playing note and active chord only when progression truly stops
                    // (not when individual chords are clicked)
                    if (playing || playbackState === 'paused') {
                      progressionWasActiveRef.current = true;
                      return;
                    }
                    if (progressionWasActiveRef.current) {
                      progressionWasActiveRef.current = false;
                      publishPlayChord({ playingNote: null, activeChordNote: null, displayedChordNote: null });
                    }
                  }}
                  onProgressionPanelToggle={() => {
                    if (activePanelId === 'progression') {
                      setActivePanelId(null);
                    } else {
                      setActivePanelId('progression');
                    }
                  }}
                  className="note-grid-container"
                  isPaintMode={isPaintMode}
                  // Onboarding coach
                  coachStep={coachStep}
                  onCoachChordTapped={onCoachChordTapped}
                  onCoachPlayTapped={onCoachPlayTapped}
                  onCoachTipDismiss={onCoachTipDismiss}
                  isDesktop={isDesktop}
                  // Metronome props
                  metronomeEnabled={metronomeEnabled}
                  metronomeBPM={metronomeBPM}
                  metronomeTimeSignature={metronomeTimeSignature}
                  metronomeVolume={metronomeVolume}
                  metronomeVisualOnly={metronomeVisualOnly}
                  onMetronomeToggle={handleMetronomeToggle}
                  onMetronomeBPMChange={handleMetronomeBPMChange}
                  onMetronomeTimeSignatureChange={handleMetronomeTimeSignatureChange}
                  onMetronomeVolumeChange={handleMetronomeVolumeChange}
                  onMetronomeVisualOnlyChange={handleMetronomeVisualOnlyChange}
                  onTapTempo={handleTapTempo}
                  onStartMetronome={handleStartMetronome}
                  barsPerChord={barsPerChord}
                  onBarsPerChordChange={handleBarsPerChordChange}
                  // Recording props
                  isRecordArmed={isRecordArmed}
                  isRecording={isRecording}
                  onRecordButtonClick={handleRecordButtonClick}
                  isCompactMode={isCompactMode}
                  // Chord extension toggle (3/7) + bass mode (config popover)
                  chordExtension={chordExtension}
                  onChordExtensionToggle={handleChordExtensionToggle}
                  onChordExtensionChange={setChordExtensionValue}
                  bassMode={bassMode}
                  onBassModeChange={setBassMode}
                />
              ))}
              </Profiler>
            </Box>

            </Box>{/* end auto-scale wrapper */}
            {activeLesson && (
              <Box sx={{ mt: 'clamp(4px, 1.5vh, 16px)', flexShrink: 0 }}>
                <Profiler id="lessonPlayer" onRender={onRenderProfile}>
                <LessonPlayer
                  key={activeLesson.id}
                  ref={lessonPlayerRef}
                  lesson={activeLesson}
                  artist={activeLessonArtist}
                  rootNote={rootNote}
                  currentMode={currentMode}
                  onClose={handleLeaveLessonToJam}
                  onPlayingStateChange={setIsLessonPlaying}
                  onTempoChange={(bpm) => { setMetronomeBPM(bpm); }}
                />
                </Profiler>
              </Box>
            )}
            </Box>{/* end .play-area */}

          </Box>

          {/* Jam et You partagent la meme enveloppe : la coquille (plafond de
              largeur, gouttiere, defilement) vit dans PageShell, a l'interieur
              de chaque page. */}
          <Box sx={{ display: currentPage === 'learn' ? 'block' : 'none', width: '100%', height: '100%' }}>
            <Profiler id="learn" onRender={onRenderProfile}>
            <Learn
              ref={learnPageRef}
              active={currentPage === 'learn'}
              rootNote={rootNote}
              currentMode={currentMode}
              currentProgression={currentProgression}
              currentMood={currentMood}
              onScalePillClick={() => setActivePanelId('rootNote')}
              onScaleChange={(newRoot, newMode) => {
                setRootNote(newRoot);
                setCurrentMode(newMode);
                setCurrentMood(null);
                setCurrentProgression(getDefaultProgression(newMode)?.name || null);
              }}
              onEnterJamMode={handleEnterJamMode}
              onEnterLesson={handleEnterLesson}
              openPackId={lessonReturnPackId}
              onOpenPackIdConsumed={clearLessonReturnPack}
              desktopBrowser={desktopBrowser}
              onStopOtherAudio={handleStopAllSounds}
              // Le dialogue d'achat de pack ouvre la meme feuille que le verrou
              // (S9) : le compte avant la caisse sur le web, la proposition de
              // compte apres l'achat.
              onRequestSignUp={(variant = 'purchase') => {
                setSignUpVariant(variant);
                setSignUpOpen(true);
              }}
            />
            </Profiler>
          </Box>

          <Box sx={{ display: currentPage === 'settings' ? 'block' : 'none', width: '100%', height: '100%' }}>
            <Profiler id="you" onRender={onRenderProfile}>
            <YouPage
              ref={youPageRef}
              active={currentPage === 'settings'}
              isDesktop={isDesktop}
              desktopBrowser={desktopBrowser}
              bassMode={bassMode}
              onBassModeChange={setBassMode}
              onUnlock={() => setUnlockSheetContext({ type: 'hub' })}
            />
            </Profiler>
          </Box>

        </Container>
        </Box>

        <ImproScalesDialog
          open={improScalesOpen}
          onClose={() => setImproScalesOpen(false)}
          baseLabel={formatScaleLabel(rootNote, currentMode)}
          progressionLabel={currentProgression && currentProgression !== RANDOM_PROGRESSION
            ? getProgressionBaseName(currentProgression)
            : null}
          scales={improScaleOptions}
          improScale={improScale}
          onPick={handleImproScalePick}
        />

        {backingScale && (
          <ImproScalesDialog
            open={backingScalesOpen}
            onClose={() => setBackingScalesOpen(false)}
            baseLabel={formatScaleLabel(backingScale.sectionRoot, backingScale.sectionScale)}
            progressionLabel={backingScale.sectionName}
            scales={backingScale.options}
            improScale={backingScale.dialogScale}
            pausedLabel={backingScale.paused ? formatScaleLabel(backingScale.sectionRoot, activeBackingTrackJamScale) : null}
            onPick={handleBackingScalePick}
          />
        )}

        <UnlockSheet
          open={!!unlockSheetContext}
          onClose={closeUnlockSheet}
          context={unlockSheetContext}
          // Ce verrou-ci est celui que le retour de l'etape compte vient de
          // rouvrir : l'identite de l'objet suffit a le dire, le contexte d'un
          // verrou ouvert a la main est toujours un objet neuf.
          initialAskedAccountFirst={!!pendingUnlockContext && unlockSheetContext === pendingUnlockContext}
          onRequestSignUp={(variant = 'beta') => {
            // Le seul depart qui peut ne jamais revenir ici : sur le web, le
            // detour par Google recharge la page et emporte tout l'etat. On
            // confie le contexte au stockage avant de laisser partir, il sera
            // relu au boot du retour (usePendingUnlock). Les autres variantes
            // succedent a un deverrouillage deja acquis, il n'y a rien a
            // reprendre.
            if (variant === 'before_purchase') markPendingUnlock(unlockSheetContext);
            setSignUpVariant(variant);
            setSignUpOpen(true);
          }}
          // On atterrit sur ce qu'on vient de deverrouiller. Avant, la feuille se
          // fermait sur la gamme d'avant et il fallait retrouver soi-meme celle
          // qu'on venait de payer.
          onUnlocked={(ctx) => {
            if (!ctx) return;
            // Depuis le hub il n'y a rien a selectionner : la carte d'achat
            // disparait d'elle-meme et on reste sur la page You.
            if (ctx.type === 'hub') return;
            if (activeBackingTrack) handleExitJamMode();
            if (activeLesson && !activeLesson.transposable) handleExitLesson({ restoreScale: false });
            if (ctx.type === 'root') {
              handleRootNoteChange(ctx.note);
              return;
            }
            if (ctx.type !== 'mode') return;
            if (ctx.rootNote && ctx.rootNote !== rootNote) handleRootNoteChange(ctx.rootNote);
            // Le mode debloque peut appartenir a un autre type d'echelle que celui
            // affiche : sans la bascule, il n'apparait meme pas dans la liste. On
            // pose le type directement, handleScaleTypeChange retomberait sur le
            // premier mode du type au lieu de celui demande.
            const scaleType = Object.keys(MODE_NAMES_BY_SCALE).find((type) =>
              MODE_NAMES_BY_SCALE[type].includes(ctx.mode)
            );
            if (scaleType && scaleType !== currentScaleType) setCurrentScaleType(scaleType);
            handleModeChange(ctx.mode);
            setActivePanelId(null);
          }}
        />

        {/* Inscription. Portee ici et pas dans UnlockSheet : elle lui succede,
            au moment ou la feuille de deverrouillage vient de se fermer. Le
            variant dit ce qui vient d'etre debloque, invitation ou achat. */}
        <SignUpSheet
          open={signUpOpen}
          onClose={() => setSignUpOpen(false)}
          variant={signUpVariant}
        />

        {/* Save Pattern Dialog */}
        <SavePatternDialog
          open={savePatternDialogOpen}
          onClose={() => setSavePatternDialogOpen(false)}
          patternData={{
            rootNote,
            currentMode,
            selectedNotes, // Now including the selected notes
            currentProgression,
            highlightTriads: false, // Would come from GuitarNeckVisualizer state
            showConnectionLines: false, // Would come from GuitarNeckVisualizer state
            timestamp: Date.now(),
            favorite: false
          }}
          onSaveSuccess={() => {
            setSavePatternSuccess(true);
            setTimeout(() => setSavePatternSuccess(false), 3000);
          }}
        />
        
        {/* Retour du login Patreon : deverrouillage, ou refus explique */}
        <Snackbar
          open={!!patreonNotice}
          autoHideDuration={6000}
          onClose={dismissPatreonNotice}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ mb: '20px' }}
        >
          <Alert onClose={dismissPatreonNotice} severity={patreonNotice?.severity || 'info'} sx={{ width: '100%' }}>
            {patreonNotice?.message}
          </Alert>
        </Snackbar>

        {/* Lien d'invitation expire ou serveur injoignable : le seul cas ou ce
            pont a quelque chose a dire. Un lien valide recharge l'app. */}
        <Snackbar
          open={!!inviteNotice}
          autoHideDuration={8000}
          onClose={dismissInviteNotice}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ mb: '20px' }}
        >
          {/* 16 px absolus : message porteur de decision (adresse du compte),
              et la racine de l'app est a 13.6 px (spec v5, Lisibilite). */}
          <Alert
            onClose={dismissInviteNotice}
            severity={inviteNotice?.severity || 'info'}
            sx={{ width: '100%', fontSize: '16px' }}
          >
            {inviteNotice?.message}
          </Alert>
        </Snackbar>

        {/* L'app revient d'un rechargement sous un compte que l'utilisateur n'a
            pas choisi a l'ecran : on nomme l'adresse, une fois. */}
        <Snackbar
          open={!!signedInNotice}
          autoHideDuration={6000}
          onClose={dismissSignedInNotice}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ mb: '20px' }}
        >
          <Alert onClose={dismissSignedInNotice} severity={signedInNotice?.severity || 'success'} sx={{ width: '100%' }}>
            {signedInNotice?.message}
          </Alert>
        </Snackbar>

        {/* Lien d'invitation en cours d'echange, a chaud comme a froid. Sans ce
            voile, l'invite lance l'app par son lien et la voit d'abord
            verrouillee, le temps du boot, de la base locale et de trois
            allers-retours reseau. Sur un reseau lent ca depasse dix secondes, et
            l'app se recharge ensuite sans prevenir. Il tient jusqu'au
            rechargement, ou le splash prend le relais.

            Le retour de Google passe sous le meme voile depuis le 29/08/2026 :
            il enchaine jusqu'a trois rechargements (refus du rattachement,
            aller-retour du repli en connexion, rechargement final) et les
            faisait a nu, alors que le hook exposait deja l'information. */}
        <Backdrop
          open={!!invitePending || oauthPending}
          sx={{ zIndex: (t) => t.zIndex.drawer + 2, color: 'white' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="inherit" />
            <Typography variant="body1">Signing you in...</Typography>
          </Box>
        </Backdrop>

        {/* Invite anonyme qui a deja sauve des patterns : le jeton de passage
            bascule l'app sur le compte choisi sur le web et purge l'identite
            locale. Le dialogue NOMME ce compte et ce qui reste derriere ;
            "Not now" ne brule aucun usage du jeton (spec v5 F10). */}
        {/* Tailles en px absolus : App.css reduit la racine a 13.6 px (85 %)
            dans toute l'app, et ce dialogue porte une decision (spec v5,
            Lisibilite : 16 px minimum, 17-18 px sur les porteurs de decision,
            cibles 44 pt). */}
        <Dialog open={!!inviteGuestConfirm} onClose={() => inviteGuestConfirm?.onCancel()} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: '18px', overflowWrap: 'anywhere' }}>
            {inviteGuestConfirm ? guestSwitchPrompt(inviteGuestConfirm.email, inviteGuestConfirm.count).title : ''}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: '16px', lineHeight: 1.55 }}>
              {inviteGuestConfirm ? guestSwitchPrompt(inviteGuestConfirm.email, inviteGuestConfirm.count).body : ''}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => inviteGuestConfirm?.onCancel()}
              sx={{ textTransform: 'none', fontSize: '15px', py: 1.25 }}
            >
              Not now
            </Button>
            <Button
              onClick={() => inviteGuestConfirm?.onContinue()}
              variant="contained"
              sx={{ textTransform: 'none', fontSize: '15px', py: 1.25 }}
            >
              Sign in
            </Button>
          </DialogActions>
        </Dialog>

        {/* Retour du login Google : erreur d'echange du code, le cas echeant */}
        <Snackbar
          open={!!oauthNotice}
          autoHideDuration={6000}
          onClose={dismissOauthNotice}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ mb: '20px' }}
        >
          <Alert onClose={dismissOauthNotice} severity={oauthNotice?.severity || 'info'} sx={{ width: '100%' }}>
            {oauthNotice?.message}
          </Alert>
        </Snackbar>

        {/* Premiere ouverture apres une mise a jour : le post release du hub
            annonce ce qui a change, une seule fois par version. En haut, hors
            du chemin des autres toasts, et decale de la zone sure : la WebView
            est en viewport-fit=cover, un Snackbar fixe passerait sous l'encoche. */}
        <Snackbar
          open={!!updateToast}
          autoHideDuration={8000}
          onClose={() => setUpdateToast(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
        >
          {/* Le fond de l'app est noir : un toast a --surface se confondait avec
              lui. Il monte donc d'un cran de clarte, avec une ombre pour le
              decoller, et le titre de la nouvelle passe devant le numero de
              version, qui ne donne envie a personne. */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: '#26262e',
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
            maxWidth: 'calc(100vw - 24px)'
          }}>
            <Box
              component="button"
              aria-label={`What's new in ${updateToast?.version || ''}: ${updateToast?.title || ''}`}
              onClick={() => {
                setCurrentPage('settings');
                setActivePanelId(null);
                dismissUpdateToast(true);
              }}
              sx={{
                border: 0,
                background: 'none',
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flex: 1,
                minWidth: 0,
                padding: '12px 4px 12px 16px'
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '15px', fontWeight: 600, color: '#fff' }}>
                  {updateToast?.title || ''}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'var(--accent)' }}>
                  {`What's new in ${updateToast?.version || ''}`}
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.55)', flexShrink: 0 }} />
            </Box>
            <IconButton
              aria-label="Dismiss"
              onClick={() => dismissUpdateToast(true)}
              sx={{ color: 'rgba(255,255,255,0.55)', mr: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Snackbar>

        {/* Pattern selection instruction snackbar */}
        <Snackbar
          open={showPatternSnackbar}
          onClose={() => setShowPatternSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ mb: '20px' }}
        >
          <Alert 
            onClose={() => setShowPatternSnackbar(false)} 
            severity="info" 
            sx={{ 
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }
            }}
          >
            Please click on the start note to fill the pattern
          </Alert>
        </Snackbar>


        {/* Bottom Navigation - Left group (Play, Inspire, Settings, Presets) + Right (Random dice) */}
        {!isCompactMode && (
        <Box
          className="bottom-nav"
          sx={{
            display: 'flex',
            position: 'fixed',
            bottom: isNative() ? `calc(10px + ${androidInset('bottom')})` : 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            height: 64,
            paddingTop: '8px',
            paddingBottom: '10px',
            paddingLeft: '16px',
            paddingRight: '6px',
            backgroundColor: 'transparent',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            pointerEvents: 'none',
            // common.css clips .bottom-nav; the coach tooltip on the Jam button
            // sits above the bar and must not be cut off.
            overflow: 'visible',
            // Navigateur de bureau : nav classique, panneau pleine hauteur colle a
            // gauche. Les pages en haut, le mixer et le de en bas.
            ...(desktopBrowser && {
              top: 0,
              bottom: 0,
              right: 'auto',
              width: DESKTOP_RAIL_WIDTH,
              height: 'auto',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#0f0f11',
              pointerEvents: 'auto'
            })
          }}
        >
          {/* Left group: Play, Inspire, Settings, Presets */}
          <Box sx={{
            display: 'flex',
            gap: 0.5,
            backgroundColor: 'rgba(20, 20, 22, 0.92)',
            borderRadius: '32px',
            padding: '6px 10px',
            pointerEvents: 'auto',
            ...(desktopBrowser && {
              flexDirection: 'column',
              gap: '4px',
              padding: 0,
              backgroundColor: 'transparent',
              borderRadius: 0
            })
          }}>
            <Box
              onClick={goToPlay}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                minWidth: 52,
                color: navPage === 'home' ? 'var(--accent)' : 'rgba(255, 255, 255, 0.7)',
                ...railItemSx(navPage === 'home')
              }}
            >
              <MusicNoteIcon sx={{ fontSize: 36 }} />
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, marginTop: '2px' }}>Play</Typography>
            </Box>
            <Box
              onClick={() => {
                // Le tip "Tap to practice" promet un backing tout de suite : on
                // saute la liste. En dehors de ce moment, on ouvre la page.
                if (coachStep === 'tapJam') {
                  launchFreeJam();
                  return;
                }
                goToPractice();
              }}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                minWidth: 52,
                color: navPage === 'learn' ? 'var(--accent)' : 'rgba(255, 255, 255, 0.7)',
                ...railItemSx(navPage === 'learn'),
                ...(coachStep === 'tapJam' && {
                  borderRadius: '12px',
                  ...coachBlinkSx
                })
              }}
            >
              {coachStep === 'tapJam' && (
                <Tip text="Tap to practice" duration={8000} onDismiss={onCoachTipDismiss} />
              )}
              <TipsAndUpdatesIcon sx={{ fontSize: 36 }} />
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, marginTop: '2px' }}>Practice</Typography>
            </Box>
            <Box
              onClick={() => {
                setCurrentPage(currentPage === 'settings' ? 'home' : 'settings');
                setActivePanelId(null);
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                minWidth: 52,
                color: currentPage === 'settings' ? 'var(--accent)' : 'rgba(255, 255, 255, 0.7)',
                ...railItemSx(currentPage === 'settings')
              }}
            >
              <Box sx={{ position: 'relative', display: 'flex' }}>
                <PersonOutlineOutlinedIcon sx={{ fontSize: 36 }} />
                <UnreadDot visible={unreadCount > 0} sx={{ top: 2, right: 2 }} />
              </Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, marginTop: '2px' }}>You</Typography>
            </Box>
            {/* Presets/Patterns button hidden for now — to be reintroduced as a feature update */}
            {false && (
            <Box
              onClick={() => {
                setActivePanelId(activePanelId === 'patterns' ? null : 'patterns');
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                minWidth: 52,
                color: activePanelId === 'patterns' ? 'var(--accent)' : 'rgba(255, 255, 255, 0.7)'
              }}
            >
              <FolderIcon sx={{ fontSize: 36 }} />
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, marginTop: '2px' }}>Presets</Typography>
            </Box>
            )}
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pointerEvents: 'auto',
            ...(desktopBrowser && { flexDirection: 'column' })
          }}>
          {desktopBrowser && <ShortcutsHelp />}
          <PlaybackMixer centered={desktopBrowser} />
          {/* Right: Random dice button - prominent with gradient ring */}
          <Box
            onClick={handleRandomSelection}
            aria-label="Surprise me: random root and scale"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              pointerEvents: 'auto',
              width: 55,
              height: 55,
              borderRadius: '50%',
              background: 'var(--surface-2)',
              boxShadow: desktopBrowser ? 'none' : 'inset 0 0 0 1px rgba(255, 255, 255, 0.14)',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              }
            }}
          >
            <CasinoIcon sx={{ fontSize: 26, color: 'white' }} />
          </Box>
          </Box>
        </Box>
        )}

        {/* Radial FAB Menu (compact mode only) */}
        {isCompactMode && (
          <RadialFabMenu
            activePanelId={activePanelId}
            setActivePanelId={setActivePanelId}
            currentPage={navPage}
            setCurrentPage={setCurrentPage}
            setTabValue={setTabValue}
            onGoToPlay={goToPlay}
            onGoToPractice={goToPractice}
            onRandomSelection={handleRandomSelection}
            unreadVisible={unreadCount > 0}
            coachStep={coachStep}
            onCoachJamTap={launchFreeJam}
            onCoachTipDismiss={onCoachTipDismiss}
          />
        )}

        {/* Drawer for Music Controls */}
        <Drawer
          anchor={isDesktop ? 'right' : 'bottom'}
          open={activePanelId !== null}
          onClose={() => setActivePanelId(null)}
          PaperProps={{
            className: `app-drawer custom-scrollbar ${activePanelId === 'patterns' ? 'fullscreen-drawer' : ''}`,
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.98)', // Override pour le menu
              width: isDesktop ? 420 : 'auto',
              height: isDesktop ? '100vh' : (activePanelId === 'patterns' ? '100vh' : 'auto'),
              top: activePanelId === 'patterns' && !isDesktop ? '0 !important' : undefined,
              borderRadius: activePanelId === 'patterns' && !isDesktop ? '0 !important' : undefined
            },
            ref: drawerPaperRef
          }}
        >
          <Box className="drawer-header">
            <Typography variant="h6" component="h3" sx={{ mb: 2, color: 'white', fontSize: '1.5rem' }} className="title1">
              {activePanelId === 'rootNote' && 'Select Scale'}
              {activePanelId === 'progression' && 'Select Progression'}
              {activePanelId === 'patterns' && 'Patterns'}
              {activePanelId === 'menu' && 'Menu'}
            </Typography>
            <IconButton 
              onClick={() => setActivePanelId(null)}
              sx={{ color: 'white' }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <Box className="drawer-content custom-scrollbar">
            {activePanelId === 'rootNote' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Un backing actif suit ses propres key centers : choisir une
                    tonalite ou une gamme ici l'arrete et applique le choix. */}
                {(activeBackingTrack || (activeLesson && !activeLesson.transposable)) && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', lineHeight: 1.4 }}>
                    {activeBackingTrack ? 'Picking a key or scale stops the backing track.' : 'Picking a key or scale closes the lesson.'}
                  </Typography>
                )}
                {/* Root Note buttons */}
                <Box>
                  <Grid container spacing={1}>
                    {ALL_NOTES.map((note) => {
                      // Tonalite payante : le bouton reste tapable et ouvre la
                      // sheet de deblocage au lieu de changer la root.
                      const lockedNote = !isRootNoteUnlocked(note);
                      return (
                      <Grid item key={note} xs={3} sm={3} md={2}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="inherit"
                          onClick={() => {
                            if (lockedNote) {
                              setUnlockSheetContext({ type: 'root', note });
                              return;
                            }
                            if (activeBackingTrack) handleExitJamMode();
                            if (activeLesson && !activeLesson.transposable) handleExitLesson({ restoreScale: false });
                            handleRootNoteChange(note);
                          }}
                          startIcon={lockedNote ? <LockIcon sx={{ fontSize: '0.7rem !important' }} /> : null}
                          sx={{
                            backgroundColor: rootNote === note ? SELECTED_BG : 'transparent',
                            borderColor: rootNote === note ? SELECTED_BG : lockedNote ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.1)',
                            color: rootNote === note ? SELECTED_FG : lockedNote ? 'rgba(255, 255, 255, 0.4)' : 'white',
                            '&:hover': {
                              backgroundColor: rootNote === note ? SELECTED_BG_HOVER : 'rgba(30, 30, 47, 0.7)',
                              borderColor: rootNote === note ? SELECTED_BG_HOVER : 'rgba(255, 255, 255, 0.3)',
                            }
                          }}
                        >
                          {ENHARMONIC_EQUIVALENTS[note] ? `${note}/${ENHARMONIC_EQUIVALENTS[note]}` : note}
                        </Button>
                      </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                <TieredScaleSelector
                  currentScaleType={currentScaleType}
                  currentMode={currentMode}
                  onScaleTypeChange={(scaleType, tier) => {
                    if (activeBackingTrack) handleExitJamMode();
                    if (activeLesson && !activeLesson.transposable) handleExitLesson({ restoreScale: false });
                    handleScaleTypeChange(scaleType, tier);
                  }}
                  onModeChange={(mode) => {
                    if (activeLesson && !activeLesson.transposable) handleExitLesson({ restoreScale: false });
                    handleModeChange(mode);
                    setActivePanelId(null);
                  }}
                  onLockedMode={(mode) => setUnlockSheetContext({ type: 'mode', mode, rootNote })}
                />
              </Box>
            )}
            {activePanelId === 'progression' && (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CasinoIcon />}
                    color="inherit"
                    onClick={handleRandomChords}
                    sx={{
                      backgroundColor: currentProgression === RANDOM_PROGRESSION ? SELECTED_BG : 'transparent',
                      borderColor: currentProgression === RANDOM_PROGRESSION ? SELECTED_BG : 'rgba(255, 255, 255, 0.1)',
                      color: currentProgression === RANDOM_PROGRESSION ? SELECTED_FG : 'white',
                      '&:hover': {
                        backgroundColor: currentProgression === RANDOM_PROGRESSION ? SELECTED_BG_HOVER : 'rgba(30, 30, 47, 0.7)',
                        borderColor: currentProgression === RANDOM_PROGRESSION ? SELECTED_BG_HOVER : 'rgba(255, 255, 255, 0.3)',
                      }
                    }}
                  >
                    AUTO
                  </Button>
                </Grid>
                {getCompatibleProgressions(currentMode).map((prog) => (
                  <Grid item key={prog} xs={12} sm={6} md={4}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="inherit"
                      onClick={() => {
                        handleProgressionChange(prog);
                        setActivePanelId(null);
                      }}
                      sx={{
                        flexDirection: 'column',
                        backgroundColor: currentProgression === prog ? SELECTED_BG : 'transparent',
                        borderColor: currentProgression === prog ? SELECTED_BG : 'rgba(255, 255, 255, 0.1)',
                        color: currentProgression === prog ? SELECTED_FG : 'white',
                        '&:hover': {
                          backgroundColor: currentProgression === prog ? SELECTED_BG_HOVER : 'rgba(30, 30, 47, 0.7)',
                          borderColor: currentProgression === prog ? SELECTED_BG_HOVER : 'rgba(255, 255, 255, 0.3)',
                        }
                      }}
                    >
                      <Box component="span">{getProgressionBaseName(prog)}</Box>
                      <Box component="span" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {getProgressionNumeralsInMode(prog, rootNote, currentMode, chordExtension)}
                      </Box>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            )}


            {activePanelId === 'patterns' && (
              <PatternsPanel
                onLoadPattern={(pattern) => {
                  // Set all relevant state from the pattern
                  if (pattern.rootNote) setRootNote(pattern.rootNote);
                  if (pattern.currentMode) setCurrentMode(pattern.currentMode);
                  if (pattern.currentMode) setCurrentMood(null); // pattern scale isn't a mood pick
                  if (pattern.selectedNotes) setSelectedNotes(pattern.selectedNotes);
                  if (Object.prototype.hasOwnProperty.call(pattern, 'currentProgression')) {
                    handleProgressionChange(pattern.currentProgression || null);
                  }

                  // Close the panel after loading
                  setActivePanelId(null);

                  // Show a success message using Snackbar
                  setSavePatternSuccess(true);
                  setTimeout(() => setSavePatternSuccess(false), 3000);

                  track('pattern_loaded', { root_note: pattern.rootNote, mode: pattern.currentMode, title: pattern.title });
                }}
                onSaveCurrentPattern={handleOpenSavePatternDialog}
                currentRootNote={rootNote}
                currentMode={currentMode}
                inDrawer={true}
              />
            )}
          </Box>
        </Drawer>
      </Box>
      </ThemeProvider>
    </DndProvider>
  );
};


// Composant principal App qui fournit le contexte
function App() {
  return (
    <Profiler id="app" onRender={onRenderProfile}>
      <AppContent />
    </Profiler>
  );
}

export default App;
